import { HSCard, HSCardSkeleton, HSSearchInput } from '@/Components/Common/HSWidget';
import FilterBar, { sanitizeFilters } from '@/Components/Common/FilterBar';
import { NewRefreshIcon } from '@/Components/Common/Icons/NewRefreshIcon';
import NewCustomButton from '@/Components/Common/NewCustomButton';
import { DEPLOYMENT_STATES, SESSION_STORAGE_KEYS } from '@/constants/device';
import { SEARCH_DEBOUNCE_MS } from '@/constants/search';
import { LoadingOutlined } from '@ant-design/icons';
import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { getPermissions } from '@vantiva-sa/homesight_humaui/Store/permisssionsStore';
import {
  KnownActionsList,
  isStaffActionAllowed,
} from '@vantiva-sa/homesight_humaui/Utilities/AccessControl';
import { Actions, DeviceStatus } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import SessionStorage from '@vantiva-sa/homesight_humaui/Utilities/SessionStorage';
import { Divider, Empty, Tooltip } from 'antd';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import MainPage from '../../MainPage';
import { FactoryResetIcon, HC200Icon, RefreshIcon } from '../Common/Icons';
import { hasOnlyStaffAdminPrivileges } from '../DeviceInventory/helpers';
import Pagination from '../Pagination/Pagination';
import { buildInventoryQueryParams, getInventoryFilterGroups } from './inventoryFilters';
import RebootResetModal from './RebootResetModal';

const INVENTORY_FILTER_LABELS = {
  connected: 'CONNECTED',
  online: 'ONLINE',
  offline: 'OFFLINE',
  accounts: 'ACCOUNTS',
  demoAccount: 'DEMO',
  deployedWithClient: 'CLIENT',
  assigned: 'ASSIGNED',
  readyToAssign: 'READYTOASSIGN',
  notActivated: 'NOTACTIVATED',
  notReady: 'NOTREADY',
};

const DevicesComponent = ({
  deviceData,
  fetchingDeviceData,
  fetchDevices,
  providerId,
  onActionClick,
  deviceCount,
  showPagination,
  offset,
  limit,
  userHubsData,
  setQueryParams,
}) => {
  const getDeploymentState = (device) => device?.deployment_state ?? null;

  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [modalData, setModalData] = useState({});
  const [showModal, setShowModal] = useState(false);
  const deviceManagementApi = useSelector((state) => state.apis.deviceManagementApi);
  const trans = useSelector(translator);
  const currentUser = useSelector(getCurrentUser);
  const isStaff = currentUser?.permission_level === 'Staff';

  const filterGroups = useMemo(
    () =>
      getInventoryFilterGroups(isStaff).map((group) => ({
        ...group,
        label: trans(INVENTORY_FILTER_LABELS[group.key]),
        children: group.children.map((item) => ({
          ...item,
          label: trans(INVENTORY_FILTER_LABELS[item.key]),
        })),
      })),
    [isStaff, trans]
  );
  const [savedFilters, setSavedFilters] = useState(
    () => SessionStorage.get(SESSION_STORAGE_KEYS.INVENTORY_FILTER) || []
  );
  const appliedFilter = useMemo(
    () => sanitizeFilters(savedFilters, filterGroups),
    [savedFilters, filterGroups]
  );
  const isFiltered = !!searchText || appliedFilter.length > 0;

  const permissions = useSelector(getPermissions());
  const actionsData = {
    reboot: {
      title: trans('REFRESH'),
      description: trans('DEVICESREBOOTDESCRIPTION'),
      action: 'device_reboot',
      icon: <RefreshIcon className="icon custom-icon-color" />,
    },
    reset: {
      title: trans('FACTORYRESET'),
      description: trans('DEVICESRESETDESCRIPTION'),
      action: 'factory_reset',
      icon: <FactoryResetIcon className="icon" style={{ fill: '#005c98' }} />,
    },
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Filter changes apply immediately; only typing is debounced.
  useEffect(() => {
    setQueryParams(buildInventoryQueryParams(appliedFilter, debouncedSearch));
  }, [appliedFilter, debouncedSearch, setQueryParams]);

  const handleSearchClear = () => {
    setSearchText('');
    setDebouncedSearch('');
  };

  const handleFilterChange = (filters) => {
    setSavedFilters(filters);
    SessionStorage.set(SESSION_STORAGE_KEYS.INVENTORY_FILTER, filters);
  };

  const cancelCallback = () => {
    setShowModal(false);
    setModalData({});
  };
  const refreshCallback = (data) => {
    performAction(data);
    setShowModal(false);
  };
  const resetCallback = (data) => {
    performAction(data);
    setShowModal(false);
  };

  const handleRefresh = (device) => {
    setShowModal(true);
    let data = {
      ...actionsData.reboot,
      deviceUuid: device.deviceuuid,
      proceed: refreshCallback,
      cancel: cancelCallback,
    };
    setModalData(data);
  };
  const handleReset = (device) => {
    setShowModal(true);
    let data = {
      ...actionsData.reset,
      deviceUuid: device.deviceuuid,
      proceed: resetCallback,
      cancel: cancelCallback,
    };
    setModalData(data);
  };

  const performAction = (data) => {
    const errorCallback = (error) => {
      // message.error("Error performing: " + data.title + " " + error.message);
      console.error('Error performing:' + data.title + ' ' + error.message);
    };
    const successCallback = (info) => {
      // message.success("Signal sent to Device for: " + data.title);
    };

    try {
      deviceManagementApi.performCommand(
        data.action,
        providerId,
        data.deviceUuid,
        { source: 'System' },
        apiCallback({
          translator: trans,
          failCallback: errorCallback,
          errorCallback,
          successCallback,
        })
      );
    } catch (error) {
      errorCallback(error);
    }
  };

  const getDeviceStatus = (device) => {
    const isNotReadyFilterApplied = appliedFilter.some((filter) => filter.itemKey === 'notReady');
    const deviceStatus = device.devicestatus?.toLowerCase();
    const provisioningStatus = device.provisioningstatus?.toLowerCase();
    const operationalStatus = device.operationalstatus?.toLowerCase();

    if (device.useruuid) {
      if (
        isNotReadyFilterApplied &&
        provisioningStatus === 'provisioned' &&
        operationalStatus === 'to_do_factoryreset'
      ) {
        return DeviceStatus.not_ready;
      }
      return DeviceStatus.userassociated;
    }

    if (
      deviceStatus === 'active' &&
      provisioningStatus === 'provisioned' &&
      operationalStatus !== 'to_do_factoryreset'
    ) {
      return DeviceStatus.provisioned;
    }

    if (provisioningStatus === 'provisioned' && operationalStatus === 'to_do_factoryreset') {
      return DeviceStatus.not_ready;
    }
    return DeviceStatus.not_activated;
  };

  const allowAssignDevice = (device, status) => {
    return (
      [DeviceStatus.provisioned, DeviceStatus.active_not_provisioned].includes(status) &&
      isStaffActionAllowed(
        currentUser?.permission_level,
        KnownActionsList.manage_devices,
        permissions
      ) &&
      device.devicestatus === 'ACTIVE'
    );
  };

  const renderDeviceStatus = (device, status) => {
    return status === DeviceStatus.userassociated ? (
      <div className="account-name">{device.accountname}</div>
    ) : status === DeviceStatus.provisioned ? (
      <div className="device-status-ready-to-assign">
        <div className="device-status-text">{trans('READYTOASSIGN')}</div>
      </div>
    ) : status === DeviceStatus.not_ready ? (
      <div className="device-status-not-ready">
        <div className="device-status-text">{trans('NOTREADY')}</div>
      </div>
    ) : (
      <div className="device-status-not-activated">
        <div className="device-status-text">{trans('NOTACTIVATED')}</div>
      </div>
    );
  };

  return (
    <>
      {showModal && <RebootResetModal data={modalData} />}
      <MainPage hasSider={true}>
        <div className="devices-container page-content-wrapper">
          <div className="page-header-container page-header-container-css">
            <span className="page-header">
              <span>{trans('DEVICES')}</span>
              <NewCustomButton
                className="cancel"
                onClick={() => fetchDevices()}
                label={trans('REFRESH')}
                icon={<NewRefreshIcon className="cancel-icon" />}
              />
            </span>
            <div className="header-action-container">
              <div className="search-container">
                <HSSearchInput
                  autoFocus
                  value={searchText}
                  onChange={setSearchText}
                  onClear={handleSearchClear}
                />
              </div>
              <FilterBar
                groups={filterGroups}
                selected={appliedFilter}
                onChange={handleFilterChange}
              />
            </div>
          </div>
          <div className="item-count-container-css  item-count-container ">
            <span className="item-count">
              {fetchingDeviceData ? <LoadingOutlined /> : deviceCount}{' '}
              {isFiltered ? trans('FOUND_L') : trans('TOTAL')}
            </span>
          </div>
          <div className="devices-list-container item-list-container">
            {fetchingDeviceData ? (
              <HSCardSkeleton />
            ) : !deviceData.length ? (
              <Empty />
            ) : (
              deviceData.map((device) => {
                let isUserAssociated = userHubsData.some((hub) => hub.useruuid === device.useruuid);
                let status = getDeviceStatus(device);
                return (
                  <HSCard
                    key={device.deviceuuid}
                    className="device-container item-container container-card-css"
                    content={
                      <>
                        <div
                          className={`container-1 ${
                            status === DeviceStatus.userassociated ? 'device-accountname' : ''
                          }`}
                          onClick={
                            status === DeviceStatus.userassociated && isUserAssociated
                              ? onActionClick(Actions.listHub, device, offset, limit)
                              : null
                          }
                        >
                          <div
                            title={
                              device.deviceconnectstatus
                                ? trans(device.deviceconnectstatus.toUpperCase())
                                : ''
                            }
                            className="device-icon-container"
                          >
                            <HC200Icon status={device.deviceconnectstatus?.toLowerCase()} />
                          </div>
                          <div className="device-sn">{device.deviceid}</div>
                          {renderDeviceStatus(device, status)}
                          {getDeploymentState(device) === DEPLOYMENT_STATES.NOT_DEPLOYED && (
                            <div className="device-demo-tag-container">
                              <span className="device-demo-tag-text">{trans('DEMO')}</span>
                            </div>
                          )}
                        </div>
                        <div className="container-2">
                          <div className="actions-container">
                            <Divider type="vertical" />
                            {/*DEPRECATED: Add account in devices from Inventory */}
                            {/* {allowAssignDevice(device, status) ? (
                          <div
                            className="action"
                            onClick={onActionClick(
                              Actions.assignDevice,
                              device,
                              offset,
                              limit
                            )}
                          >
                            <Tooltip title={trans("ADDACCOUNT")}>
                              <div>
                                <AccountAddIcon className="account-add-icon" />
                              </div>
                            </Tooltip>
                          </div>
                        ) : (
                          <div className="empty-action" />
                        )} */}
                            {![DeviceStatus.not_activated].includes(status) ? (
                              <div className="action">
                                <Tooltip title={actionsData.reboot.title}>
                                  <div>
                                    <RefreshIcon onClick={() => handleRefresh(device)} />
                                  </div>
                                </Tooltip>
                              </div>
                            ) : (
                              <div className="empty-action" />
                            )}
                            {![DeviceStatus.not_activated].includes(status) &&
                            hasOnlyStaffAdminPrivileges(currentUser?.permission_level) ? (
                              <div className="action">
                                <Tooltip title={actionsData.reset.title}>
                                  <div>
                                    <FactoryResetIcon onClick={() => handleReset(device)} />
                                  </div>
                                </Tooltip>
                              </div>
                            ) : (
                              <div className="empty-action" />
                            )}
                          </div>
                        </div>
                      </>
                    }
                  />
                );
              })
            )}
            {showPagination && (
              <Pagination
                deviceCount={deviceCount}
                limit={limit}
                offset={offset}
                onActionClick={onActionClick}
              />
            )}
          </div>
        </div>
      </MainPage>
    </>
  );
};

DevicesComponent.propTypes = {
  deviceData: PropTypes.arrayOf(PropTypes.object),
  fetchingDeviceData: PropTypes.bool,
  fetchDevices: PropTypes.func.isRequired,
  onActionClick: PropTypes.func,
  providerId: PropTypes.string,
  deviceCount: PropTypes.number,
  showPagination: PropTypes.bool,
  offset: PropTypes.number,
  limit: PropTypes.number,
  userHubsData: PropTypes.arrayOf(PropTypes.object),
  setQueryParams: PropTypes.func.isRequired,
};

export default DevicesComponent;
